import type { StepSpec } from '@dru/ui';

/**
 * A tube's journey through the laboratory, read the way the bench reads it:
 * where is this sample, what has to happen next, and who does it.
 *
 * The moves mirror the API's sample machine (laboratory/services/
 * sample.service.ts), including the permission each one needs. The transition
 * endpoint itself also requires `lab.sample-accession`. Offering a move the
 * server will refuse teaches people that errors are normal, so a move this
 * login cannot make is not offered; the server stays the authority either way.
 */

export interface SampleMove {
  to: string;
  label: string;
  /** Beyond `lab.sample-accession`, which every move through /transition needs. */
  need?: string;
  /**
   * A shortcut around the bench's real next act. Marking a tube resulted by
   * hand is for results that arrived another way; the ordinary path is to
   * enter the values, which marks the tube resulted on its own.
   */
  manual?: boolean;
}

export interface SampleJourney {
  steps: StepSpec[];
  now: string;
  /** The act that moves the tube forward, when it is a bench act. */
  primary: SampleMove | null;
  /** Other valid moves from here, shown quieter than the primary one. */
  secondary: SampleMove[];
  /** Whether a rejection (with a coded reason) is valid from here. */
  rejectable: boolean;
  /** Who acts next, when nobody at this screen can. */
  waitingOn: string | null;
  /**
   * Whether entering values is the next act for this login: the tube is on
   * the bench (received or processing) and this login holds `lab.result-enter`. When it is, a screen
   * leads with result entry and shows a `manual` primary quieter.
   */
  enterResults: boolean;
}

const STEPS = [
  { key: 'collected', label: 'Collected' },
  { key: 'received', label: 'Received' },
  { key: 'processing', label: 'Processing' },
  { key: 'resulted', label: 'Resulted' },
  { key: 'validated', label: 'Validated' },
  { key: 'released', label: 'Released' },
] as const;
type Key = typeof STEPS[number]['key'];

const AT: Record<string, {
  step: Key | 'end';
  detail?: string;
  now: string;
  primary?: SampleMove;
  secondary?: SampleMove[];
  waitingOn?: string;
}> = {
  ORDERED: {
    step: 'collected', now: 'Waiting to be drawn', detail: 'Replacement ordered',
    primary: { to: 'COLLECTED', label: 'Record collection' },
  },
  COLLECTED: {
    step: 'received', now: 'Drawn, not yet at the bench',
    primary: { to: 'RECEIVED', label: 'Receive at the bench' },
    secondary: [{ to: 'IN_TRANSIT', label: 'Send to the lab' }],
  },
  IN_TRANSIT: {
    step: 'received', now: 'On its way to the lab', detail: 'In transit',
    primary: { to: 'RECEIVED', label: 'Receive at the bench' },
  },
  RECEIVED: {
    step: 'processing', now: 'Received, not started', detail: 'Not started',
    primary: { to: 'PROCESSING', label: 'Start processing' },
  },
  PROCESSING: {
    step: 'processing', now: 'Being processed', detail: 'Under way',
    // Entering the last value marks the tube resulted on its own; this is the
    // manual path for a tube whose results arrive another way.
    primary: { to: 'RESULTED', label: 'Mark resulted', need: 'lab.result-enter', manual: true },
  },
  RESULTED: { step: 'validated', now: 'Results entered', waitingOn: 'A pathologist validates' },
  VALIDATED: { step: 'released', now: 'Validated', waitingOn: 'A pathologist releases' },
  RELEASED: { step: 'end', now: 'Released to the physician' },
  REJECTED: { step: 'collected', now: 'Rejected', detail: 'Rejected — recollection follows' },
  RECOLLECT_REQUESTED: { step: 'collected', now: 'Rejected', detail: 'Recollection requested' },
};

const REJECTABLE = new Set(['COLLECTED', 'IN_TRANSIT', 'RECEIVED', 'PROCESSING']);
const BLOCKED = new Set(['REJECTED', 'RECOLLECT_REQUESTED']);

export function sampleJourney(status: string, permissions: ReadonlySet<string>): SampleJourney {
  const at = AT[status] ?? { step: 'collected' as const, now: status.replace(/_/g, ' ').toLowerCase() };
  const current = at.step === 'end' ? STEPS.length : STEPS.findIndex((s) => s.key === at.step);
  const blocked = BLOCKED.has(status);

  const steps: StepSpec[] = STEPS.map((s, i) => ({
    key: s.key,
    label: s.label,
    state: i < current ? 'done' : i === current ? (blocked ? 'blocked' : 'current') : 'upcoming',
    ...(i === current && at.detail ? { detail: at.detail } : {}),
  }));

  const can = (m: SampleMove) => permissions.has('lab.sample-accession') && (!m.need || permissions.has(m.need));
  const primary = at.primary && can(at.primary) ? at.primary : null;
  const secondary = (at.secondary ?? []).filter(can);
  const rejectable = REJECTABLE.has(status)
    && permissions.has('lab.sample-accession') && permissions.has('lab.sample-reject');
  const waitingOn = at.waitingOn
    ?? (at.primary && !primary ? 'Waiting on the lab bench' : null);

  // A received tube is entered straight away: the first value starts it and
  // the last marks it resulted, so "Start processing" is not a step the bench
  // has to take first. It stays offered, quieter, for a tube worked elsewhere.
  const enterResults = (status === 'RECEIVED' || status === 'PROCESSING') && permissions.has('lab.result-enter');

  return { steps, now: at.now, primary, secondary, rejectable, waitingOn, enterResults };
}

/** Samples that need the bench before anything else can happen, most urgent first. */
export const SAMPLE_ORDER: Record<string, number> = {
  RECEIVED: 0, PROCESSING: 1, IN_TRANSIT: 2, COLLECTED: 3, ORDERED: 4,
  RESULTED: 5, VALIDATED: 6, REJECTED: 7, RECOLLECT_REQUESTED: 8, RELEASED: 9,
};
